import React from "react";

import { StatusTag, Tabs } from "@graffer-inc/console-design-kit";

import { BasicInfo } from "./tab-contents/basic-info";
import { ServiceAccountKeys } from "./tab-contents/service-account-keys/service-account-keys";

import type {
  ServiceAccount,
  ServiceAccountKey,
} from "../../../../application/domain/service-account/service-account";

type Props = {
  serviceAccount: ServiceAccount;
  serviceAccountKeys: ServiceAccountKey[];
  groups: { id: string; name: string }[];

  onClickServiceAccountKeyCreate: (
    param: { expiredAtUnixMilliSecond: number; name: string },
    callback: {
      onSucceeded: (newKey: ServiceAccountKey, privateKeyPem: string) => void;
      onFailed: (errorMessage: string) => void;
    },
  ) => void;
  onClickUpdateServiceAccount: (
    param: Pick<ServiceAccount, "description" | "roles" | "groupsRoles">,
    callback: {
      onSucceeded: (updated: ServiceAccount) => void;
      onFailed: (errorMessage: string) => void;
    },
  ) => void;

  // Props が与えられたときに限り導線をオープンする
  onClickDisableServiceAccount?: (
    param: { serviceAccountId: ServiceAccount["id"] },
    callback: {
      onSucceeded: () => void;
      onFailed: (errorMessage: string) => void;
    },
  ) => void;
  // Props が与えられたときに限り導線をオープンする
  onClickDisableServiceAccountKey?: (
    param: { serviceAccountKeyId: ServiceAccountKey["id"] },
    callback: {
      onSucceeded: () => void;
      onFailed: (errorMessage: string) => void;
    },
  ) => void;
};

export const ServiceAccountDetailContent: React.FC<Props> = ({
  serviceAccount,
  serviceAccountKeys,
  groups,
  onClickServiceAccountKeyCreate,
  onClickUpdateServiceAccount,
  onClickDisableServiceAccount,
  onClickDisableServiceAccountKey,
}) => {
  return (
    <>
      <Title
        accountName={serviceAccount.accountName}
        disabled={serviceAccount.disabled}
      />
      <hr />
      <Content
        serviceAccount={serviceAccount}
        groups={groups}
        serviceAccountKeys={serviceAccountKeys}
        onClickServiceAccountKeyCreate={onClickServiceAccountKeyCreate}
        onClickUpdateServiceAccount={onClickUpdateServiceAccount}
        onClickDisableServiceAccount={onClickDisableServiceAccount}
        onClickDisableServiceAccountKey={onClickDisableServiceAccountKey}
      />
    </>
  );
};

const Title: React.FC<{ accountName: string; disabled: boolean }> = ({
  accountName,
  disabled,
}) => {
  return (
    <div className="columns is-vcentered">
      <div className="column has-text-left">
        <h1 className="title is-flex is-align-items-center">
          {accountName}
          <span className="ml-4">
            {disabled ? (
              <StatusTag type="ERROR">無効</StatusTag>
            ) : (
              <StatusTag type="SUCCESS">有効</StatusTag>
            )}
          </span>
        </h1>
      </div>
    </div>
  );
};

const Content: React.FC<Props> = ({
  serviceAccount,
  groups,
  serviceAccountKeys,
  onClickServiceAccountKeyCreate,
  onClickUpdateServiceAccount,
  onClickDisableServiceAccount,
  onClickDisableServiceAccountKey,
}) => {
  const [activeTab, setActiveTab] = React.useState<
    "basicInfo" | "serviceAccountKeys"
  >("basicInfo");

  const tabItems = React.useMemo<
    React.ComponentProps<typeof Tabs>["tabItems"]
  >(() => {
    return [
      {
        label: "基本情報",
        onClick: () => {
          setActiveTab("basicInfo");
        },
        isActive: activeTab === "basicInfo",
        hidden: false,
      },
      {
        label: "サービスアカウントキー",
        onClick: () => {
          setActiveTab("serviceAccountKeys");
        },
        isActive: activeTab === "serviceAccountKeys",
        hidden: false,
      },
    ];
  }, [activeTab, setActiveTab]);

  return (
    <>
      <Tabs tabItems={tabItems} />
      <div className={activeTab === "basicInfo" ? "" : "is-hidden"}>
        <BasicInfo
          serviceAccount={serviceAccount}
          groups={groups}
          onClickUpdateServiceAccount={onClickUpdateServiceAccount}
          onClickDisableServiceAccount={onClickDisableServiceAccount}
        />
      </div>

      <div className={activeTab === "serviceAccountKeys" ? "" : "is-hidden"}>
        <ServiceAccountKeys
          serviceAccount={serviceAccount}
          serviceAccountKeys={serviceAccountKeys}
          onClickCreate={onClickServiceAccountKeyCreate}
          onClickDisableServiceAccountKey={onClickDisableServiceAccountKey}
        />
      </div>
    </>
  );
};
